import { useState, useEffect, useRef } from 'react';
import { trustStats } from '../data/mock';
import { Award, Users, TrendingUp, Globe } from 'lucide-react';

const iconMap = { Award, Users, TrendingUp, Globe };

// Animated counter hook
const useAnimatedCounter = (target, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [target, duration, started]);

  return { ref, count };
};

const StatCard = ({ stat, index }) => {
  const Icon = iconMap[stat.icon];
  const { ref, count } = useAnimatedCounter(stat.value, 2000);

  return (
    <div ref={ref} className="group">
      <div className="glass rounded-2xl p-6 lg:p-8 text-center hover:bg-white/[0.08] transition-[background-color,box-shadow,transform] duration-400 hover:-translate-y-2 hover:shadow-xl hover:shadow-[#D4A853]/5 cursor-default card-tilt">
        <div className="w-14 h-14 mx-auto mb-4 bg-[#D4A853]/10 rounded-xl flex items-center justify-center group-hover:bg-[#D4A853]/20 transition-colors duration-300">
          <Icon className="w-7 h-7 text-[#E8C87A]" />
        </div>
        <p
          className="text-3xl sm:text-4xl font-bold text-white mb-2"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {count}{stat.suffix}
        </p>
        <p className="text-white/50 text-sm font-medium">{stat.label}</p>
      </div>
    </div>
  );
};

export const TrustBlock = () => {
  return (
    <section className="relative py-20 bg-gradient-to-b from-[#0F1B2D] to-[#0A1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {trustStats.map((stat, index) => (
            <StatCard key={stat.label} stat={stat} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
